import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { Notify } from 'quasar';
import { useI18n } from 'vue-i18n';
import { useStrapiGet, useStrapiPost } from 'shared/composables/useStrapiApi';
import { useAssignments } from 'shared/composables/useAssignments';
import { useAuth } from 'src/boot/auth';

/**
 * Teacher preview registration: pressing "Preview" on an assigned course /
 * section card registers the teacher into the REAL course engine (their own
 * user-course / standalone user-section record, exactly like a student's) and
 * navigates into the content. Create-or-reuse — the backend enforces one
 * user-course per user+course, and a second click must just resume.
 */
export function useTeacherPreview() {
  const router = useRouter();
  const { t } = useI18n();
  const { currentUser } = useAuth();
  const { fetchUserCourses, fetchUserSections } = useAssignments();

  /** documentId of the entity whose registration round-trip is in flight. */
  const previewLoadingDocumentId = ref('');

  async function findMyUserCourse(
    courseDocumentId: string,
  ): Promise<string | undefined> {
    const { execute, data } = useStrapiGet('user-courses-me', {
      offset: 0,
      filters: { course: { documentId: courseDocumentId } },
    });
    await execute();
    return data.value?.data?.[0]?.documentId ?? undefined;
  }

  async function previewCourse(courseDocumentId: string, courseId?: number) {
    if (previewLoadingDocumentId.value) return;
    previewLoadingDocumentId.value = courseDocumentId;
    try {
      let userCourseDocId = await findMyUserCourse(courseDocumentId);

      if (!userCourseDocId) {
        const userId = currentUser.value?.id;
        if (!userId || !courseId) throw new Error('missing user or course id');
        const { execute } = useStrapiPost('user-courses', {
          data: {
            user: { connect: [{ id: userId }] },
            course: { connect: [{ id: courseId }] },
          },
        });
        await execute();
        userCourseDocId = await findMyUserCourse(courseDocumentId);
      }

      if (!userCourseDocId) throw new Error('registration not found');

      // Hydrate the repo with the fully-populated record (course sections,
      // steps, …) — the course view reads it from there. The fresh unstarted
      // registration sorts into page 1.
      await fetchUserCourses({ offset: 0 });

      await router.push({
        name: 'course.sections',
        params: { userCourseDocId },
      });
    } catch (error) {
      console.error('previewCourse:', error);
      Notify.create({ type: 'negative', message: t('PREVIEW_FAILED') });
    } finally {
      previewLoadingDocumentId.value = '';
    }
  }

  async function findMyStandaloneUserSection(
    sectionDocumentId: string,
  ): Promise<string | undefined> {
    const { execute, data } = useStrapiGet('user-sections-me', {
      sectionDocumentId,
      userCourseId: -1,
    });
    await execute();
    return data.value?.[0]?.[0]?.documentId ?? undefined;
  }

  async function previewSection(sectionDocumentId: string) {
    if (previewLoadingDocumentId.value) return;
    previewLoadingDocumentId.value = sectionDocumentId;
    try {
      let userSectionDocId =
        await findMyStandaloneUserSection(sectionDocumentId);

      if (!userSectionDocId) {
        const { execute, data } = useStrapiPost('user-sections', {
          data: { sectionDocumentId, user_course_id: -1 },
        });
        await execute();
        userSectionDocId = data.value?.data?.documentId ?? undefined;
        if (!userSectionDocId) {
          // Recovery: create may have raced a previous registration.
          userSectionDocId =
            await findMyStandaloneUserSection(sectionDocumentId);
        }
      }

      if (!userSectionDocId) throw new Error('registration not found');

      // Hydrate the repo with the fully-populated record for the steps view.
      await fetchUserSections(userSectionDocId);

      await router.push({
        name: 'section.steps',
        params: { userSectionDocId },
      });
    } catch (error) {
      console.error('previewSection:', error);
      Notify.create({ type: 'negative', message: t('PREVIEW_FAILED') });
    } finally {
      previewLoadingDocumentId.value = '';
    }
  }

  return {
    previewLoadingDocumentId,
    previewCourse,
    previewSection,
  };
}
